import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  ShieldCheck, 
  MessageSquare, 
  Palette, 
  FileText, 
  Sparkles,
  BadgeCheck
} from "lucide-react";

const WhyChooseSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const reasons = [
    { icon: BadgeCheck, text: "Certifierade elektriker" },
    { icon: MessageSquare, text: "Tydlig kommunikation" },
    { icon: Palette, text: "Designfokus i varje projekt" },
    { icon: FileText, text: "Noggrann dokumentation" },
    { icon: Sparkles, text: "Hög finish på alla arbeten" },
    { icon: ShieldCheck, text: "Garanti på alla installationer" },
  ];

  return (
    <section
      data-testid="why-choose-section"
      ref={ref}
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="aspect-[4/5] relative overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/lsd4rud6_20220922_155749.jpg"
                alt="Optimal EL elektriker i arbete"
                className="w-full h-full object-cover"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
            </div>
            
            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="absolute -bottom-6 -right-6 md:bottom-8 md:right-8 bg-[#D4AF37] p-6 md:p-8"
            >
              <div className="text-black text-center">
                <div className="text-4xl font-manrope font-light">10+</div>
                <div className="text-sm uppercase tracking-wider">År i branschen</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Content */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4"
            >
              Varför Välja Oss
            </motion.p>

            <motion.h2
              data-testid="why-choose-title"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-3xl md:text-4xl lg:text-5xl font-manrope font-light text-white mb-6 tracking-tight"
            >
              Precision
              <br />
              <span className="text-[#D4AF37]">och passion.</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-zinc-400 text-base md:text-lg leading-relaxed mb-10"
            >
              När du väljer Optimal EL väljer du kvalitet utan kompromisser. 
              Vi tar oss tid att förstå dina behov och levererar resultat 
              som håller i generationer.
            </motion.p>

            {/* Reasons Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {reasons.map((reason, index) => (
                <motion.div
                  key={index}
                  data-testid={`why-choose-reason-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex items-center gap-3 p-4 bg-[#050505] border border-white/5"
                >
                  <reason.icon className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-zinc-300 text-sm">{reason.text}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
